import { post } from 'services/request';

export type IEmbeddingPoint = {
  external_id: string;
  dialect_id: number;
  placeholder_signature: string;
  pattern_embed_text: string;
  pattern_clean: string;
};

type RenderFn = () => void;

// Simple batch uploader for Semantic Unifier embeddings, inspired by existing batch models
export default class MemorySemanticUnifierBatchModel {
  render: RenderFn;
  onComplete: (() => void) | null = null;
  private queue: IEmbeddingPoint[] = [];
  private index = 0;
  private _failed = 0;
  private _running = false;
  private _paused = false;
  private _chunkSize = 100;

  constructor(options: { render: RenderFn; chunkSize?: number }) {
    this.render = options.render;
    if (options.chunkSize && options.chunkSize > 0) this._chunkSize = options.chunkSize;
  }

  load(points: IEmbeddingPoint[]) {
    this.queue = Array.isArray(points) ? points : [];
    this.index = 0;
    this._failed = 0;
    this._paused = false;
    this._running = false;
    this.render();
    // Fire completion callback if everything has been processed
    if (this.pending === 0 && this.onComplete) {
      const cb = this.onComplete;
      this.onComplete = null;
      try { cb(); } catch {}
    }
  }

  async start() {
    if (this.total === 0) return;
    this._running = true;
    this._paused = false;
    this.render();
    while (this._running && !this._paused && this.index < this.total) {
      const start = this.index;
      const end = Math.min(this.index + this._chunkSize, this.total);
      const chunk = this.queue.slice(start, end);
      try {
        await post<any>(`memory-semantic-unifier/embeddings/upsert`, { points: chunk });
      } catch (e) {
        this._failed += chunk.length;
      }
      this.index = end;
      this.render();
    }
    this._running = false;
    this.render();
  }

  pause() {
    this._paused = true;
    this.render();
  }
  resume() {
    if (this.index < this.total) {
      this._paused = false;
      this._running = true;
      this.render();
      this.start();
    }
  }
  stop() {
    this._running = false;
    this._paused = false;
    this.render();
  }

  get total() {
    return this.queue.length;
  }
  get done() {
    return this.index;
  }
  get failed() {
    return this._failed;
  }
  get pending() {
    return Math.max(0, this.total - this.index);
  }
  get running() {
    return this._running;
  }
  get paused() {
    return this._paused;
  }

  get chunkSize() {
    return this._chunkSize;
  }

  // Percent of completed items
  get percent(): number {
    if (this.total === 0) return 0;
    return (this.done / this.total) * 100;
  }
}
